import { useState, useEffect } from 'react';
import { matchPointsService, type MatchPointsData, type TeamStanding, type TournamentStandingsConfig, type CreateOrUpdateTournamentStandingsConfig } from '../services/matchPointsService';

export interface UseMatchPointsResult {
  matchPoints: any[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export interface UseTournamentStandingsResult {
  standings: TeamStanding[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export interface UseStandingsConfigResult {
  config: TournamentStandingsConfig | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export interface UseAllStandingsConfigsResult {
  configs: TournamentStandingsConfig[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to get all tournament standings configurations
 */
export const useAllStandingsConfigs = (tournamentId: string): UseAllStandingsConfigsResult => {
  const [configs, setConfigs] = useState<TournamentStandingsConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfigs = async () => {
    if (!tournamentId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await matchPointsService.getAllStandingsConfigs(tournamentId);
      setConfigs(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch standings configs');
      console.error('useAllStandingsConfigs error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, [tournamentId]);

  return {
    configs,
    loading,
    error,
    refetch: fetchConfigs
  };
};

/**
 * Hook to get all match points for the current organization
 */
export const useAllMatchPoints = (): UseMatchPointsResult => {
  const [matchPoints, setMatchPoints] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMatchPoints = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await matchPointsService.getAllMatchPoints();
      setMatchPoints(data);
    } catch (err) {
      setError('Error fetching match points');
      console.error('Error in useAllMatchPoints:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatchPoints();
  }, []);

  return {
    matchPoints,
    loading,
    error,
    refetch: fetchMatchPoints
  };
};

/**
 * Hook to get match points for a specific match
 */
export const useMatchPoints = (matchId: string): UseMatchPointsResult => {
  const [matchPoints, setMatchPoints] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMatchPoints = async () => {
    if (!matchId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await matchPointsService.getMatchPoints(matchId);
      setMatchPoints(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch match points');
      console.error('useMatchPoints error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatchPoints();
  }, [matchId]);

  return {
    matchPoints,
    loading,
    error,
    refetch: fetchMatchPoints
  };
};

/**
 * Hook to get tournament standings
 */
export const useTournamentStandings = (tournamentId: string, seasonId?: string): UseTournamentStandingsResult => {
  const [standings, setStandings] = useState<TeamStanding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStandings = async () => {
    if (!tournamentId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await matchPointsService.getTournamentStandings(tournamentId, seasonId);
      setStandings(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch standings');
      console.error('useTournamentStandings error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStandings();
  }, [tournamentId, seasonId]);

  return {
    standings,
    loading,
    error,
    refetch: fetchStandings
  };
};

/**
 * Hook to get tournament standings configuration
 */
export const useStandingsConfig = (tournamentId: string, seasonId?: string): UseStandingsConfigResult => {
  const [config, setConfig] = useState<TournamentStandingsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = async () => {
    if (!tournamentId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await matchPointsService.getStandingsConfig(tournamentId, seasonId);
      setConfig(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch standings config');
      console.error('useStandingsConfig error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, [tournamentId, seasonId]);

  return {
    config,
    loading,
    error,
    refetch: fetchConfig
  };
};

/**
 * Hook for managing match points operations
 */
export const useMatchPointsOperations = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createMatchPoints = async (data: MatchPointsData) => {
    try {
      setLoading(true);
      setError(null);
      await matchPointsService.createMatchPoints(data);
    } catch (err: any) {
      setError(err.message || 'Failed to create match points');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateMatchPoints = async (matchId: string, data: Omit<MatchPointsData, 'matchId'>) => {
    try {
      setLoading(true);
      setError(null);
      await matchPointsService.updateMatchPoints(matchId, data);
    } catch (err: any) {
      setError(err.message || 'Failed to update match points');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteMatchPoints = async (matchId: string) => {
    try {
      setLoading(true);
      setError(null);
      await matchPointsService.deleteMatchPoints(matchId);
    } catch (err: any) {
      setError(err.message || 'Failed to delete match points');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createOrUpdateStandingsConfig = async (config: CreateOrUpdateTournamentStandingsConfig) => {
    try {
      setLoading(true);
      setError(null);
      return await matchPointsService.createOrUpdateStandingsConfig(config);
    } catch (err: any) {
      setError(err.message || 'Failed to create/update standings config');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateTieBreakerOrder = async (configId: string, tieBreakerOrder: string[]) => {
    try {
      setLoading(true);
      setError(null);
      return await matchPointsService.updateTieBreakerOrder(configId, tieBreakerOrder);
    } catch (err: any) {
      setError(err.message || 'Failed to update tie-breaker order');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateMatchMapping = async (configId: string, matchIds: string[]) => {
    try {
      setLoading(true);
      setError(null);
      return await matchPointsService.updateMatchMapping(configId, matchIds);
    } catch (err: any) {
      setError(err.message || 'Failed to update match mapping');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getMatchesFromMatchPoints = async (tournamentId: string, seasonId?: string) => {
    try {
      setLoading(true);
      setError(null);
      return await matchPointsService.getMatchesFromMatchPoints(tournamentId, seasonId);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch matches from match points');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getTournamentStandings = async (tournamentId: string, seasonId?: string) => {
    try {
      setLoading(true);
      setError(null);
      return await matchPointsService.getTournamentStandings(tournamentId, seasonId);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch tournament standings');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getGroupStandings = async (tournamentId: string, seasonId?: string) => {
    try {
      setLoading(true);
      setError(null);
      return await matchPointsService.getGroupStandings(tournamentId, seasonId);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch group standings');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    createMatchPoints,
    updateMatchPoints,
    deleteMatchPoints,
    createOrUpdateStandingsConfig,
    updateTieBreakerOrder,
    updateMatchMapping,
    getMatchesFromMatchPoints,
    getTournamentStandings,
    getGroupStandings
  };
};

export interface UseGroupStandingsResult {
  groupStandings: Map<string, TeamStanding[]>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to get group standings for a specific tournament and season
 */
export const useGroupStandings = (tournamentId: string, seasonId?: string): UseGroupStandingsResult => {
  const [groupStandings, setGroupStandings] = useState<Map<string, TeamStanding[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchGroupStandings = async () => {
    if (!tournamentId) {
      setGroupStandings(new Map());
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await matchPointsService.getGroupStandings(tournamentId, seasonId);
      setGroupStandings(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch group standings');
      console.error('useGroupStandings error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroupStandings();
  }, [tournamentId, seasonId]);

  return {
    groupStandings,
    loading,
    error,
    refetch: fetchGroupStandings
  };
};
